import {
    chartRefreshScheduler,
    computeStaleState,
    formatAge,
    FrameBudgetTracker,
    measureFrame,
    RingBuffer,
    getStaleThresholdMs,
    setSnapshot,
    getSnapshot,
    reportChartRenderMetric,
} from '../../charts/engine';
import { renderDonutChart, renderHeatmapGrid, renderLineChart, renderTreemapChart, type TreemapItem } from '../../charts/primitives';
import { fetchPriorityHeadlines, type NewsItem } from '../../services/news-feed';
import { fetchCurrencySummary } from '../../services/currency-data';
import { fetchCommoditySummary } from '../../services/commodity-data';
import { fetchIndices } from '../../services/market-data';
import { isFeatureEnabled } from '../../services/runtime-config';

type WorldSnapshot = {
    headlines: NewsItem[];
    riskSeries: number[];
    contagionSeries: number[];
    lastUpdatedAt: number;
};

type RegionCode = 'NA' | 'EU' | 'APAC' | 'MEA' | 'LATAM' | 'IND';

const REGIONS: RegionCode[] = ['NA', 'EU', 'APAC', 'MEA', 'LATAM', 'IND'];

const REGION_KEYWORDS: Record<RegionCode, string[]> = {
    NA: ['us ', 'united states', 'america', 'washington', 'nasdaq', 'dow', 'federal reserve', 'fed'],
    EU: ['europe', 'eu ', 'eurozone', 'brussels', 'germany', 'france', 'uk', 'london', 'ecb'],
    APAC: ['china', 'japan', 'asia', 'apac', 'hong kong', 'taiwan', 'korea', 'beijing'],
    MEA: ['middle east', 'gulf', 'uae', 'saudi', 'iran', 'israel', 'africa', 'red sea'],
    LATAM: ['latam', 'latin america', 'brazil', 'mexico', 'argentina', 'chile'],
    IND: ['india', 'nifty', 'sensex', 'rupee', 'rbi', 'mumbai', 'delhi'],
};

const teardownMap = new WeakMap<HTMLElement, () => void>();

function sentimentScore(item: NewsItem): number {
    const s = (item.sentiment || '').toLowerCase();
    if (s.includes('positive') || s.includes('bull')) return 2;
    if (s.includes('negative') || s.includes('bear')) return -2;

    const text = `${item.title} ${item.description}`.toLowerCase();
    if (/(war|attack|missile|crash|sanction|conflict|outbreak)/.test(text)) return -3;
    if (/(deal|rally|growth|expands|ceasefire|recovery)/.test(text)) return 2;
    return 0;
}

function computeRiskIndex(headlines: NewsItem[]): number {
    if (headlines.length === 0) return 0;
    const total = headlines.reduce((sum, item) => sum + sentimentScore(item), 0);
    return Number((total / headlines.length).toFixed(2));
}

function categoryBuckets(headlines: NewsItem[]): number[] {
    const bucket = new Map<string, number>();
    headlines.forEach((item) => {
        const key = (item.category || 'general').toLowerCase();
        bucket.set(key, (bucket.get(key) || 0) + 1);
    });
    return Array.from(bucket.values());
}

function extractRegions(item: NewsItem): RegionCode[] {
    const text = `${item.title} ${item.description}`.toLowerCase();
    const found = REGIONS.filter((region) => REGION_KEYWORDS[region].some((token) => text.includes(token)));
    return found.length > 0 ? found : ['APAC'];
}

function headlineSeverity(item: NewsItem): number {
    const score = Math.abs(sentimentScore(item));
    const text = `${item.title} ${item.description}`.toLowerCase();
    const shockHits = (text.match(/(sanction|attack|missile|default|tariff|embargo|blockade|military|cyber|oil shock)/g) || []).length;
    return Math.max(0.5, score + (shockHits * 0.75));
}

function computeAssetShock(assets: TreemapItem[]): number {
    if (assets.length === 0) return 0;
    const avgAbsMove = assets.reduce((sum, asset) => sum + Math.abs(Number(asset.change || 0)), 0) / assets.length;
    return Math.max(0, avgAbsMove);
}

function buildDiffusion(headlines: NewsItem[], assetShock: number): {
    cells: Array<{ x: number; y: number; value: number }>;
    vectors: Array<{ from: RegionCode; to: RegionCode; score: number }>;
    contagionLevel: number;
} {
    const direct = new Map<RegionCode, number>(REGIONS.map((region) => [region, 0]));
    const vectors = new Map<string, number>();

    headlines.slice(0, 24).forEach((item) => {
        const regions = extractRegions(item);
        const severity = headlineSeverity(item);
        const spreadMultiplier = regions.length > 1 ? 1.25 : 1;

        regions.forEach((region) => {
            direct.set(region, (direct.get(region) || 0) + (severity * spreadMultiplier));
        });

        for (let i = 0; i < regions.length; i += 1) {
            for (let j = i + 1; j < regions.length; j += 1) {
                const left = regions[i];
                const right = regions[j];
                const key = `${left}->${right}`;
                vectors.set(key, (vectors.get(key) || 0) + severity * 0.9);
            }
        }
    });

    const amplification = 1 + Math.min(1.8, assetShock / 2.2);
    const cells = REGIONS.flatMap((region, idx) => {
        const directStress = (direct.get(region) || 0) * amplification;
        const transmission = Array.from(vectors.entries())
            .filter(([key]) => key.startsWith(`${region}->`) || key.includes(`->${region}`))
            .reduce((sum, [, value]) => sum + value, 0) * (0.6 + (assetShock / 6));

        return [
            { x: idx, y: 0, value: -Math.min(5, directStress / 7.5) },
            { x: idx, y: 1, value: -Math.min(5, transmission / 14) },
        ];
    });

    const topVectors = Array.from(vectors.entries())
        .map(([key, score]) => {
            const [from, to] = key.split('->') as [RegionCode, RegionCode];
            return { from, to, score: score * amplification };
        })
        .sort((a, b) => b.score - a.score)
        .slice(0, 6);

    const contagionLevel = cells.length
        ? Math.abs(cells.reduce((sum, cell) => sum + cell.value, 0)) / cells.length
        : 0;

    return { cells, vectors: topVectors, contagionLevel };
}

function classifyContagionRegime(contagionLevel: number): 'CONTAINED' | 'ELEVATED' | 'SYSTEMIC' {
    if (contagionLevel >= 2.2) return 'SYSTEMIC';
    if (contagionLevel >= 1.1) return 'ELEVATED';
    return 'CONTAINED';
}

export function renderPhase2WorldMonitorEngine(container: HTMLElement): void {
    if (!isFeatureEnabled('charts_v2_engine')) {
        container.innerHTML = '<div class="wm-empty">charts_v2_engine flag is disabled.</div>';
        return;
    }

    teardownMap.get(container)?.();

    container.innerHTML = `
      <div class="v2-chart-card glass-card">
        <div class="card-header">
          <h3>Phase 2 Engine: World Monitor</h3>
          <span class="text-xs text-muted">Tier B + Tier C refresh</span>
        </div>

        <div class="v2-chart-grid">
          <div class="v2-chart-slot">
            <div class="v2-chart-title">Global Risk Index</div>
            <canvas id="v2-world-risk" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Headline Category Mix</div>
            <canvas id="v2-world-category" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Sentiment Grid</div>
            <canvas id="v2-world-sentiment" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Cross-Asset Pulse</div>
            <div id="v2-world-asset-map"></div>
          </div>

                    <div class="v2-chart-slot">
                        <div class="v2-chart-title">Contagion Propagation</div>
                        <canvas id="v2-world-contagion" class="v2-chart-canvas"></canvas>
                    </div>

                    <div class="v2-chart-slot">
                        <div class="v2-chart-title">Stress Diffusion Matrix</div>
                        <canvas id="v2-world-diffusion" class="v2-chart-canvas"></canvas>
                    </div>

          <div class="v2-chart-slot v2-chart-slot--wide">
            <div class="v2-chart-title">Top Global Headlines</div>
            <div id="v2-world-headlines" class="v2-sector-board"></div>
          </div>

                    <div class="v2-chart-slot v2-chart-slot--wide">
                        <div class="v2-chart-title">Top Contagion Vectors</div>
                        <div id="v2-world-vectors" class="v2-sector-board"></div>
                    </div>
        </div>

        <div class="v2-chart-meta">
          <span id="v2-world-freshness" class="text-xs">Waiting for live global feed...</span>
          <span id="v2-world-frame" class="text-xs text-muted" style="margin-left: var(--space-3);">Frame: --</span>
        </div>

        <div id="v2-world-stale" class="v2-stale-overlay" style="display:none;">
          World data delayed. Rendering cached snapshot.
        </div>
      </div>
    `;

    const riskCanvas = container.querySelector('#v2-world-risk') as HTMLCanvasElement | null;
    const categoryCanvas = container.querySelector('#v2-world-category') as HTMLCanvasElement | null;
    const sentimentCanvas = container.querySelector('#v2-world-sentiment') as HTMLCanvasElement | null;
    const assetMapNode = container.querySelector('#v2-world-asset-map') as HTMLElement | null;
    const contagionCanvas = container.querySelector('#v2-world-contagion') as HTMLCanvasElement | null;
    const diffusionCanvas = container.querySelector('#v2-world-diffusion') as HTMLCanvasElement | null;
    const headlinesNode = container.querySelector('#v2-world-headlines') as HTMLElement | null;
    const vectorsNode = container.querySelector('#v2-world-vectors') as HTMLElement | null;
    const freshnessNode = container.querySelector('#v2-world-freshness') as HTMLElement | null;
    const frameNode = container.querySelector('#v2-world-frame') as HTMLElement | null;
    const staleNode = container.querySelector('#v2-world-stale') as HTMLElement | null;

    if (!riskCanvas || !categoryCanvas || !sentimentCanvas || !assetMapNode || !contagionCanvas || !diffusionCanvas || !headlinesNode || !vectorsNode || !freshnessNode || !frameNode || !staleNode) return;

    const snapshotKey = 'phase2:world-monitor-engine';
    const staleThresholdMs = getStaleThresholdMs('B');
    const frameTracker = new FrameBudgetTracker(180, 16);
    const riskSeries = new RingBuffer<number>(180);
    const contagionSeries = new RingBuffer<number>(180);

    let lastUpdatedAt: number | null = null;
    let lastHeadlines: NewsItem[] = [];

    const draw = async (headlines: NewsItem[], shouldAdvanceContagion: boolean) => {
        const sample = measureFrame('world-monitor-engine', () => {
            renderLineChart(riskCanvas, riskSeries.toArray(), '#ff9f43');

            const categoryMix = categoryBuckets(headlines);
            renderDonutChart(categoryCanvas, categoryMix.length ? categoryMix : [1], ['#5aa6ff', '#15b57a', '#ff9f43', '#d94736', '#7cc4fa']);

            const cells = headlines.slice(0, 12).map((item, idx) => ({
                x: idx % 6,
                y: Math.floor(idx / 6),
                value: sentimentScore(item),
            }));
            renderHeatmapGrid(sentimentCanvas, cells, 6, 2);

            const top = headlines.slice(0, 10);
            headlinesNode.innerHTML = top.length
                ? top.map((item) => {
                    const score = sentimentScore(item);
                    const tone = score > 0 ? 'terminal-text--up' : score < 0 ? 'terminal-text--down' : '';
                    return `<div class="terminal-list__row"><span>${item.title}</span><strong class="${tone}">${item.source} · ${item.timeAgo}</strong></div>`;
                }).join('')
                : '<div class="wm-empty">No live world headlines available.</div>';

            renderLineChart(contagionCanvas, contagionSeries.toArray(), '#d94736');
        });

        const [currency, commodity, indices] = await Promise.all([
            fetchCurrencySummary(),
            fetchCommoditySummary(),
            fetchIndices(),
        ]);

        const assetTiles: TreemapItem[] = [];
        if (currency.usdInr) {
            assetTiles.push({ label: 'USD/INR', value: Math.max(1, currency.usdInr.rate), change: currency.usdInr.pChange });
        }
        if (currency.eurInr) {
            assetTiles.push({ label: 'EUR/INR', value: Math.max(1, currency.eurInr.rate), change: currency.eurInr.pChange });
        }
        if (commodity.goldINR) {
            assetTiles.push({ label: 'GOLD', value: Math.max(1, commodity.goldINR.price), change: commodity.goldINR.pChange });
        }
        if (commodity.crudeOil) {
            assetTiles.push({ label: 'CRUDE', value: Math.max(1, commodity.crudeOil.price), change: commodity.crudeOil.pChange });
        }

        indices.slice(0, 4).forEach((idx) => {
            assetTiles.push({
                label: idx.symbol,
                value: Math.max(1, idx.last),
                change: idx.pChange,
            });
        });

        renderTreemapChart(assetMapNode, assetTiles.slice(0, 10));

        const assetShock = computeAssetShock(assetTiles);
        const diffusion = buildDiffusion(headlines, assetShock);
        const contagionRegime = classifyContagionRegime(diffusion.contagionLevel);

        renderHeatmapGrid(diffusionCanvas, diffusion.cells, 6, 2);
        if (shouldAdvanceContagion) {
            contagionSeries.push(diffusion.contagionLevel);
        }
        renderLineChart(contagionCanvas, contagionSeries.toArray(), '#d94736');

        vectorsNode.innerHTML = diffusion.vectors.length
            ? [
                `<div class="terminal-list__row"><span>Regime</span><strong class="${contagionRegime === 'SYSTEMIC' ? 'terminal-text--down' : contagionRegime === 'CONTAINED' ? 'terminal-text--up' : ''}">${contagionRegime} ${diffusion.contagionLevel.toFixed(2)}</strong></div>`,
                ...diffusion.vectors.map((row) => {
                const tone = row.score >= 3 ? 'terminal-text--down' : row.score >= 1.5 ? '' : 'terminal-text--up';
                return `<div class="terminal-list__row"><span>${row.from} → ${row.to}</span><strong class="${tone}">${row.score.toFixed(2)}</strong></div>`;
                }),
            ].join('')
            : '<div class="wm-empty">No contagion vectors detected.</div>';

        frameTracker.push(sample.label, sample.durationMs);
        const avg = frameTracker.getAverageMs();
        const within = frameTracker.isWithinBudget(sample.durationMs);
        frameNode.textContent = `Frame: ${sample.durationMs.toFixed(2)}ms | avg ${avg.toFixed(2)}ms | budget ${within ? 'OK' : 'OVER'}`;

        reportChartRenderMetric({
            id: 'phase2-world-monitor',
            label: 'World Monitor',
            latestMs: sample.durationMs,
            averageMs: avg,
            budgetMs: frameTracker.getBudgetMs(),
            withinBudget: within,
        });

        const stale = computeStaleState(lastUpdatedAt, staleThresholdMs);
        staleNode.style.display = stale.stale ? 'flex' : 'none';
        freshnessNode.textContent = stale.stale
            ? `Stale: ${formatAge(stale.ageMs)} old`
            : `Live update ${new Date(lastUpdatedAt || Date.now()).toLocaleTimeString('en-IN')}`;
    };

    const persist = () => {
        setSnapshot<WorldSnapshot>(snapshotKey, {
            headlines: lastHeadlines,
            riskSeries: riskSeries.toArray(),
            contagionSeries: contagionSeries.toArray(),
            lastUpdatedAt: lastUpdatedAt || Date.now(),
        });
    };

    const update = async () => {
        try {
            const headlines = await fetchPriorityHeadlines();
            if (!headlines.length) throw new Error('no world news');

            const risk = computeRiskIndex(headlines);
            riskSeries.push(risk);
            lastHeadlines = headlines;
            lastUpdatedAt = Date.now();
            await draw(headlines, true);
            persist();
        } catch {
            if (lastHeadlines.length > 0) {
                await draw(lastHeadlines, false);
            }
        }
    };

    const persisted = getSnapshot<WorldSnapshot>(snapshotKey);
    if (persisted?.headlines?.length) {
        lastHeadlines = persisted.headlines;
        persisted.riskSeries.forEach((value) => riskSeries.push(value));
        persisted.contagionSeries?.forEach((value) => contagionSeries.push(value));
        lastUpdatedAt = persisted.lastUpdatedAt;
        void draw(lastHeadlines, false);
    }

    const taskId = `phase2-world-monitor-${Math.random().toString(36).slice(2)}`;
    const unregister = chartRefreshScheduler.register(taskId, 'B', () => {
        void update();
    }, 5000);

    teardownMap.set(container, () => {
        unregister();
    });
}
